import { Dispatch } from '@reduxjs/toolkit';
import DOMPurify from 'dompurify';
import Cookies from 'universal-cookie';

import { PromptResponse, REAPPLICATION_ACCOUNT_STATUS } from '../api/api';
import { ENV } from '../app/apiScreenSlice';
import {
  MODAL_ENUM,
  requireFullSSN,
  setEligibilityVerified,
  setFullNumberEntered,
  setInStoreDevice,
  setShowModal,
  setShowWelcomeBackCard,
} from '../app/applicationScreenSlice';
import {
  setAppStatus,
  setReApplicationInApp,
} from '../app/applicationStatusSlice';
import {
  resetConsumerInfoSlice,
  setConsumerFactor,
  setConsumerProfile,
} from '../app/consumerInfoSlice';
import { setMultiCardUser } from '../app/loginSlice';
import { DisplayAmount } from '../app/merchantDataSlice';
import {
  setIncorrectOtpCode,
  setReachedMaxRetries,
} from '../app/otpScreenSlice';
import { SCREEN, setScreen } from '../app/screenStateSlice';
import { RootState } from '../app/store';
import { resetInputValidation } from '../app/validatorSlice';
import { PARAM_IN_STORE, PARAM_SHOW_WPP } from '../assets/consts/inStoreConsts';
import { ANALYTICS } from '../firebase/firebase';
import { LoadHomeResponse } from '../types';
import { COOKIEKEY, DOMAIN } from './cookieConstants';
import { LANGUAGE_HEADER, LanguageCode } from './language';
import { ZendeskUrl } from './merchantLinks';
import { merchantInfoByProductUUID } from './productUUIDConstants';
import { PAGE } from './routeConstant';
import { SESSION_STORAGE } from './sessionStorageConstants';

export const clearHomePageState = (dispatch: Dispatch) => {
  dispatch(setMultiCardUser(false));
  dispatch(setShowWelcomeBackCard(false));
  dispatch(setAppStatus(''));
  dispatch(requireFullSSN(false));
  dispatch(resetConsumerInfoSlice());
  dispatch(setFullNumberEntered(false));
  dispatch(resetInputValidation());
  dispatch(setEligibilityVerified(false));
  dispatch(setShowModal(MODAL_ENUM.noModal));
  dispatch(setScreen(SCREEN.APPLICATION));
  dispatch(setReApplicationInApp(false));
  return;
};

export const handleRedirectCookie = (
  token: string,
  deviceID: string,
  consumerUUID: string,
  productUUID: string,
  isMultiCard: string
) => {
  const domain = window.location.hostname.toLowerCase();
  const domainParse = domain.split('.');
  if (domain === ENV.LOCAL) {
    saveCookie(COOKIEKEY.JWT, token);
    saveCookie(COOKIEKEY.DEVICEID, deviceID);
    saveCookie(COOKIEKEY.CONSUMERUUID, consumerUUID);
    saveCookie(COOKIEKEY.REDIRECTED, 'true');
    saveCookie(COOKIEKEY.PRODUCT_UUID, productUUID);
    saveCookie(COOKIEKEY.ISMULTICARD, isMultiCard);
  } else if (domainParse.includes(ENV.STG)) {
    saveCookie(COOKIEKEY.JWT, token, DOMAIN.STG);
    saveCookie(COOKIEKEY.DEVICEID, deviceID, DOMAIN.STG);
    saveCookie(COOKIEKEY.CONSUMERUUID, consumerUUID, DOMAIN.STG);
    saveCookie(COOKIEKEY.REDIRECTED, 'true', DOMAIN.STG);
    saveCookie(COOKIEKEY.PRODUCT_UUID, productUUID, DOMAIN.STG);
    saveCookie(COOKIEKEY.ISMULTICARD, isMultiCard, DOMAIN.STG);
  } else if (domainParse.includes(ENV.PREPRD)) {
    saveCookie(COOKIEKEY.JWT, token, DOMAIN.PREPRD);
    saveCookie(COOKIEKEY.DEVICEID, deviceID, DOMAIN.PREPRD);
    saveCookie(COOKIEKEY.CONSUMERUUID, consumerUUID, DOMAIN.PREPRD);
    saveCookie(COOKIEKEY.REDIRECTED, 'true', DOMAIN.PREPRD);
    saveCookie(COOKIEKEY.PRODUCT_UUID, productUUID, DOMAIN.PREPRD);
    saveCookie(COOKIEKEY.ISMULTICARD, isMultiCard, DOMAIN.PREPRD);
  } else {
    saveCookie(COOKIEKEY.JWT, token, DOMAIN.PROD);
    saveCookie(COOKIEKEY.DEVICEID, deviceID, DOMAIN.PROD);
    saveCookie(COOKIEKEY.CONSUMERUUID, consumerUUID, DOMAIN.PROD);
    saveCookie(COOKIEKEY.REDIRECTED, 'true', DOMAIN.PROD);
    saveCookie(COOKIEKEY.PRODUCT_UUID, productUUID, DOMAIN.PROD);
    saveCookie(COOKIEKEY.ISMULTICARD, isMultiCard, DOMAIN.PROD);
  }
};

export const getCookieValueFor = (key: string) => {
  // Cookies currently in format: 'key1=value1; key2=value2; ...;'
  const cookies = new Cookies();
  return cookies.get(key);
};

export const removeCookieFor = (key: string) => {
  const cookies = new Cookies();
  const domain = window.location.hostname.toLowerCase();
  const domainParse = domain.split('.');
  const path = '/';
  if (domain === ENV.LOCAL) {
    return cookies.remove(key);
  } else if (domainParse.includes(ENV.STG)) {
    return cookies.remove(key, { path: path, domain: DOMAIN.STG });
  } else if (domainParse.includes(ENV.PREPRD)) {
    return cookies.remove(key, { path: path, domain: DOMAIN.PREPRD });
  } else {
    return cookies.remove(key, { path: path, domain: DOMAIN.PROD });
  }
};

export const saveCookie = (key: string, value: string, domain?: string) => {
  // key-value that you want to add will be overwritten if it existed previously or not.
  const cookieMaxAgeSecs = 5 * 60;
  if (domain !== undefined) {
    document.cookie = `${key}=${value}; path=/; secure; sameSite=Lax; domain=${domain}; max-age=${cookieMaxAgeSecs}`;
  } else {
    // for localhost test
    document.cookie = `${key}=${value}; path=/; secure; sameSite=Lax; max-age=${cookieMaxAgeSecs}`;
  }
};

export const sanitize = (url: string) => {
  // strip special  characters
  return DOMPurify.sanitize(url).replace(
    /[^a-zA-Z0-9-._~:/?#[@\]!$&'*+,;=]/g,
    ''
  );
};

export const validateUrlOrReturnEmpty = (url: string) => {
  // URL validation
  const regex =
    /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/gi;
  const isValid = regex.test(url);
  return isValid ? url : '';
};

export const setSessionUrlParam = (link: string, dispatch: Dispatch) => {
  const urlSearchParams = new URLSearchParams(link);
  if (urlSearchParams.get(PARAM_IN_STORE) === 'true') {
    // consume the query parameter
    dispatch(setInStoreDevice(true));
    urlSearchParams.delete(PARAM_IN_STORE);
  }

  const uniqueKeys = new Set(urlSearchParams.keys());
  if (uniqueKeys.size !== 0) {
    uniqueKeys.forEach((key) => {
      const values = urlSearchParams.getAll(key).join(',');
      const clean = sanitize(values);
      sessionStorage.setItem(key, clean);
    });
    sessionStorage.setItem(
      SESSION_STORAGE.FULL_QUERY,
      urlSearchParams.toString()
    );
  }
};

export const setSessionLanguage = (language: string, env: string) => {
  switch (env) {
    case ENV.STG:
      saveCookie(LANGUAGE_HEADER, language, DOMAIN.STG);
      break;
    case ENV.PREPRD:
      saveCookie(LANGUAGE_HEADER, language, DOMAIN.PREPRD);
      break;
    case ENV.PROD:
      saveCookie(LANGUAGE_HEADER, language, DOMAIN.PROD);
      break;
    default:
      saveCookie(LANGUAGE_HEADER, language);
  }
};

export const getLanguageFromCookie = (): LanguageCode => {
  const languageVal = getCookieValueFor(COOKIEKEY.LANGUAGE_HEADER);
  switch (languageVal) {
    case LanguageCode.Spanish:
      return LanguageCode.Spanish;
    default:
      return LanguageCode.English;
  }
};

export const convertAmountToString = (am: DisplayAmount) => {
  if (!am) {
    return '';
  }
  return am.displaySymbol + am.displayValue;
};

export const clearServiceLoginState = (dispatch: Dispatch) => {
  dispatch(setIncorrectOtpCode(false));
  dispatch(setReachedMaxRetries(false));
  return;
};

export const reApplicationSetUpAfterPrompt = (
  promptData: PromptResponse,
  dispatch: Dispatch,
  getState: () => RootState
) => {
  const consumerProfile = getState().consumerInfoSelector.consumerProfile;
  const consumerFactor = getState().consumerInfoSelector.consumerFactor;

  if (promptData.consumer) {
    dispatch(
      setConsumerProfile({
        ...consumerProfile,
        address: promptData.consumer.address,
        name: {
          firstName: promptData.consumer.name.firstName,
          lastName: promptData.consumer.name.lastName,
        },
        phone: promptData.consumer.phone,
        email: promptData.consumer.email,
        dob: promptData.consumer.dob,
      })
    );
  }
  if (promptData.consumerFactors) {
    dispatch(
      setConsumerFactor({
        ...consumerFactor,
        income: promptData.consumerFactors.income,
        incomeType: promptData.consumerFactors.incomeType,
        housingType: promptData.consumerFactors.housingType,
        housingCost: promptData.consumerFactors.housingCost,
      })
    );
  }
  // single card user would be flagged as rejection/frozen in prompt response
  dispatch(setMultiCardUser(false));
  dispatch(setShowWelcomeBackCard(true));
  dispatch(setShowModal(MODAL_ENUM.noModal));
  clearServiceLoginState(dispatch);
};

export const reApplicationSetUpAfterHome = (
  dispatch: Dispatch,
  homeData?: LoadHomeResponse,
  accountStatus?: string
) => {
  switch (accountStatus) {
    case REAPPLICATION_ACCOUNT_STATUS.EXPIRED_OFFER:
      if (
        homeData &&
        homeData.actions.length === 1 &&
        !(homeData.productAccounts && homeData.productAccounts.length >= 1)
      ) {
        dispatch(setMultiCardUser(false));
      }
      break;
  }
  dispatch(setShowWelcomeBackCard(true));
  dispatch(setShowModal(MODAL_ENUM.noModal));
};

export const getContactSupportLink = (
  productUUID: string,
  selectedLanguage: string
): string => {
  const selectedLanguageCode = selectedLanguage as LanguageCode;
  return (
    merchantInfoByProductUUID[productUUID]?.zendesk[selectedLanguageCode] ??
    ZendeskUrl.DEFAULT
  );
};

export const redirectTo = (
  label: string = 'account',
  newTab: boolean = false,
  inStoreOnboarding: boolean = false,
  findAccount: boolean = false
) => {
  const domain = window.location.hostname.toLowerCase();
  const domainParse = domain.split('.');
  ANALYTICS.analyticsUtil.writeStoredEvents();
  let url = '';
  if (domain === ENV.LOCAL) {
    // TODO: for local test: set url as localhost port
    url = 'http://localhost:8081';
  } else if (domainParse.includes(ENV.STG)) {
    url = 'https://' + label + DOMAIN.STG;
  } else if (domainParse.includes(ENV.PREPRD)) {
    url = 'https://' + label + DOMAIN.PREPRD;
  } else {
    url = 'https://' + label + DOMAIN.PROD;
  }
  if (inStoreOnboarding) {
    url += `?${PARAM_SHOW_WPP}=true`;
  } else if (findAccount) {
    url += PAGE.FIND_ACCOUNT;
  }
  if (newTab) {
    window.open(url, '_blank');
  } else {
    window.location.replace(url);
  }
};

export const redirectToAccountWithCookie = (
  token: string,
  deviceID: string,
  consumerUUID: string,
  productUUID: string,
  isMultiCard: string,
  inStoreOnboarding: boolean = false
) => {
  handleRedirectCookie(token, deviceID, consumerUUID, productUUID, isMultiCard);
  redirectTo('account', false, inStoreOnboarding);
};

export const getOfferUUIDs = () => {
  if (
    sessionStorage &&
    sessionStorage.getItem(SESSION_STORAGE.OFFER_CONFIG_UUIDS) !== ''
  ) {
    return sessionStorage
      .getItem(SESSION_STORAGE.OFFER_CONFIG_UUIDS)
      ?.split(',')
      .map((item) => item.trim());
  }
  return [];
};

export const getPreapprovalCodeFromSession = () => {
  if (
    sessionStorage &&
    sessionStorage.getItem(SESSION_STORAGE.PREAPPROVAL_CODE) !== ''
  ) {
    return sessionStorage.getItem(SESSION_STORAGE.PREAPPROVAL_CODE);
  }
};

export const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
  e.preventDefault();

  // Dismiss keyboard when submitting
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }
};
